import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Check, Star, ArrowRight } from 'lucide-react';
import FadeInSection from '../components/FadeInSection';
import { useCms } from '../context/CmsContext';
import { cocktailImageUrl } from '../utils/cocktailImage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const { t, img } = useCms();
  const HERO_IMAGE = img('home.hero');
  const PARALLAX_1 = img('home.parallax1');
  const PARALLAX_2 = img('home.parallax2');
  const [popularCocktails, setPopularCocktails] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fadeIn, setFadeIn] = useState(false);

  useEffect(() => {
    setTimeout(() => setFadeIn(true), 100);
    const fetchData = async () => {
      try {
        const [cocktailsRes, reviewsRes] = await Promise.all([
          axios.get(`${API_URL}/api/cocktails?populair=true`),
          axios.get(`${API_URL}/api/reviews`),
        ]);
        setPopularCocktails(cocktailsRes.data.slice(0, 7));
        setReviews(reviewsRes.data.slice(0, 3));
      } catch {
        // Silent fail — sections gracefully render empty state
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const cocktailImages = {
    '1': 'https://images.unsplash.com/photo-1765227548281-2fa23b56ad3e?w=400&h=300&fit=crop',
    '2': 'https://images.unsplash.com/photo-1663908672815-fd62829ad3f7?w=400&h=300&fit=crop',
    '3': 'https://images.unsplash.com/photo-1723742479315-bb20d13225dd?w=400&h=300&fit=crop',
    '4': 'https://images.unsplash.com/photo-1607446045710-d5a8fd9bc1db?w=400&h=300&fit=crop',
    '5': 'https://images.unsplash.com/photo-1503726800290-d5f1a00e05b0?w=400&h=300&fit=crop',
    '6': 'https://images.unsplash.com/photo-1758218058958-78f40a716c20?w=400&h=300&fit=crop',
    '7': 'https://images.unsplash.com/photo-1713964596186-5ddfbcb7a349?w=400&h=300&fit=crop',
  };

  const usps = [
    { title: 'Professionele Bartenders', description: 'Ervaren en gecertificeerd' },
    { title: 'Verse Ingrediënten', description: 'Alleen de beste kwaliteit' },
    { title: 'Vanaf €6 per Cocktail', description: 'Eerlijke en vaste prijzen' },
    { title: 'Snelle Service', description: 'Geen lange wachttijden' },
    { title: 'Perfect voor Elk Feest', description: 'Van klein tot groot' },
    { title: 'Mobiele Bar', description: 'Wij komen naar jou toe' },
  ];

  return (
    <div>
      {/* Hero — extends behind transparent navbar */}
      <section
        className={`relative min-h-screen flex items-center justify-center text-white overflow-hidden parallax transition-all duration-1000 ease-out ${
          fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
        style={{ backgroundImage: `url("${HERO_IMAGE}")` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/70"></div>

        <div className="text-center z-10 px-4 relative max-w-4xl pt-20">
          <p className="uppercase tracking-[0.4em] text-xs sm:text-sm text-white/80 mb-4">
            Mobiele Cocktailservice
          </p>
          <h1
            className="font-serif-display text-6xl sm:text-7xl lg:text-8xl mb-6 drop-shadow-2xl"
            data-testid="home-hero-title"
          >
            {t('home.hero_title', 'Cocktails op Locatie')}
          </h1>
          <p
            className="text-lg sm:text-xl mb-10 max-w-2xl mx-auto font-light text-white/95"
            data-testid="home-hero-subtitle"
          >
            {t('home.hero_subtitle', '')}
          </p>
          <div className="flex sm:hidden justify-center">
            <Link to="/offerte" className="btn-primary text-base">
              Vraag Offerte Aan
            </Link>
          </div>
        </div>

        {/* Subtle scroll hint */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70 text-xs tracking-[0.3em] uppercase animate-pulse">
          Scroll
        </div>
      </section>

      {/* Introductie */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <FadeInSection>
          <div className="text-center">
            <p className="uppercase tracking-[0.3em] text-xs text-cm-primary mb-4">Welkom</p>
            <h2
              className="font-serif-display text-5xl sm:text-6xl mb-8 text-cm-primary"
              data-testid="home-intro-title"
            >
              {t('home.intro_title', 'Welkom bij Cocktailmannetje')}
            </h2>
            <p
              className="text-lg text-gray-700 leading-relaxed whitespace-pre-line"
              data-testid="home-intro-text"
            >
              {t('home.intro_text', '')}
            </p>
          </div>
        </FadeInSection>
      </section>

      {/* Parallax quote section */}
      <section
        className="parallax relative h-[60vh] flex items-center justify-center text-white"
        style={{ backgroundImage: `url("${PARALLAX_1}")` }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        <FadeInSection>
          <blockquote className="relative z-10 max-w-3xl px-6 text-center font-serif-display italic text-3xl sm:text-4xl lg:text-5xl leading-snug drop-shadow-lg">
            {t('home.parallax_quote', '“Geen feest is hetzelfde — elke cocktail vertelt een verhaal.”')}
          </blockquote>
        </FadeInSection>
      </section>

      {/* Waarom kiezen voor ons */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInSection>
            <div className="text-center mb-14">
              <p className="uppercase tracking-[0.3em] text-xs text-cm-primary mb-4">Waarom Cocktailmannetje</p>
              <h2 className="font-serif-display text-5xl sm:text-6xl text-cm-primary">
                Waarom Kiezen voor Ons?
              </h2>
            </div>
          </FadeInSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {usps.map((usp, index) => (
              <FadeInSection key={index} delay={index * 80}>
                <div className="bg-white p-8 rounded-2xl shadow-elegant hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-gray-100 h-full">
                  <div className="bg-cm-primary w-12 h-12 rounded-full flex items-center justify-center mb-5">
                    <Check className="text-white" size={22} />
                  </div>
                  <h3 className="font-serif-display text-2xl mb-2 text-cm-primary">{usp.title}</h3>
                  <p className="text-gray-600 font-light">{usp.description}</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Beach club experience parallax */}
      <section
        className="parallax relative py-32"
        style={{ backgroundImage: `url("${PARALLAX_2}")` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/55 to-black/30"></div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-white">
          <FadeInSection>
            <p className="uppercase tracking-[0.3em] text-xs text-white/80 mb-4">De Ervaring</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mb-6 drop-shadow-lg">
              {t('home.experience_title', 'Een Beach Club Ervaring op Jouw Locatie')}
            </h2>
            <p className="text-lg text-white/95 leading-relaxed font-light">
              {t('home.experience_text', '')}
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* Populaire cocktails */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 bg-gray-50">
        <FadeInSection>
          <div className="text-center mb-14">
            <p className="uppercase tracking-[0.3em] text-xs text-cm-primary mb-4">Onze Signature Drinks</p>
            <h2 className="font-serif-display text-5xl sm:text-6xl mb-3 text-cm-primary">
              Onze Populaire Cocktails
            </h2>
            <p className="text-gray-600 text-lg font-light">
              Van klassieke favorites tot tropische creaties
            </p>
          </div>
        </FadeInSection>

        {loading ? (
          <div className="text-center py-20">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {popularCocktails.map((cocktail) => (
              <div
                key={cocktail.id}
                className="bg-white rounded-2xl shadow-elegant overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <div
                  className="h-64 bg-cm-primary flex items-center justify-center"
                  style={{
                    backgroundImage: `url("${cocktailImageUrl(cocktail)}")`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                ></div>
                <div className="p-6">
                  <h3 className="font-serif-display text-2xl mb-2 text-cm-primary">{cocktail.naam}</h3>
                  <p className="text-gray-600 text-sm font-light">{cocktail.beschrijving}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="text-center mt-12">
          <Link to="/menu" className="btn-primary inline-flex items-center gap-2">
            Bekijk Volledig Menu <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* Reviews */}
      <section className="bg-cm-primary text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInSection>
            <h2 className="font-serif-display text-5xl sm:text-6xl text-center mb-14">
              Wat Klanten Zeggen
            </h2>
          </FadeInSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {reviews.map((review) => (
              <div key={review.id} className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/10">
                <div className="flex gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} size={18} fill="#FFB84D" color="#FFB84D" />
                  ))}
                </div>
                <p className="text-lg mb-4 italic font-light leading-relaxed">&ldquo;{review.tekst}&rdquo;</p>
                <p className="font-medium tracking-wide">— {review.naam}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
